import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight, ExternalLink, Search, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { WeightLabel } from '../components/ArtworkTile'
import { CategoryBadge } from '../components/ConceptBadges'
import { ConceptGraph } from '../components/ConceptGraph'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { categoryColor } from '../components/categoryStyle'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { useConcept, useConceptGraph, useConcepts, useGraphOverview } from '../hooks/useKnowledge'
import type { GraphOverview } from '../api/knowledgeApi'

const RELATION_LABEL: Record<string, [string, string]> = {
  broader_than: ['broader than', 'narrower than'],
  variant_of: ['variant of', 'has variant'],
  opposite_of: ['opposite of', 'opposite of'],
  related_to: ['related to', 'related to'],
}

/** A concept you can walk to: click re-centres the explorer on it. */
function WalkChip({ c, onWalk, extra }: { c: { ucid: string; name: string; category: string | null; artworkCount?: number }; onWalk: (ucid: string, name: string) => void; extra?: string }) {
  return (
    <button
      onClick={() => onWalk(c.ucid, c.name)}
      className="inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 text-xs hover:bg-slate-50"
      title={c.category ?? 'uncategorised'}
    >
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: categoryColor(c.category) }} />
      <span className="max-w-[14rem] truncate">{c.name}</span>
      {(extra ?? c.artworkCount) != null && <span className="tabular-nums text-slate-400">{extra ?? c.artworkCount?.toLocaleString()}</span>}
    </button>
  )
}

/** Search concepts and every name merged into them; results open in the explorer. */
function ConceptSearch({ onPick }: { onPick: (ucid: string, name: string) => void }) {
  const [q, setQ] = useState('')
  const query = useDebouncedValue(q, 200)
  const { data, isFetching } = useConcepts({ q: query || undefined, limit: 12, sort: 'artworks' })
  return (
    <div className="relative">
      <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
      <Input
        className="h-10 pl-9 text-base"
        placeholder="Search a concept — a style, subject, mood, technique, or any name merged into one…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      {q.length > 0 && query.length > 0 && data && (
        <div className={`absolute z-20 mt-1 max-h-96 w-full overflow-auto rounded-md border bg-white shadow-lg ${isFetching ? 'opacity-80' : ''}`}>
          {data.data.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No concept matches “{query}”.</div>}
          {data.data.map((c) => {
            const alias = query && !c.name.toLowerCase().includes(query.toLowerCase())
              ? c.memberNames.find((m) => m.toLowerCase().includes(query.toLowerCase()))
              : null
            return (
              <button
                key={c.ucid}
                onClick={() => { onPick(c.ucid, c.name); setQ('') }}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-sm">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: categoryColor(c.category) }} />
                    <span className="truncate">{c.name}</span>
                    <span className="text-xs text-slate-400">{c.category ?? 'uncategorised'}</span>
                  </span>
                  {alias && <span className="ml-4 block truncate text-xs text-slate-500">matched “{alias}”</span>}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-slate-500">{c.artworkCount.toLocaleString()} artworks · {c.degree} links</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Categories as bars sized by artwork links (concept count alongside); click one to see its concepts. */
function CategoryBars({ overview, selected, onSelect }: { overview: GraphOverview; selected: string | null; onSelect: (c: string | null) => void }) {
  const max = Math.max(1, ...overview.categories.map((c) => c.artworkLinks))
  return (
    <div className="space-y-1">
      {overview.categories.map((c) => (
        <button
          key={c.category}
          onClick={() => onSelect(selected === c.category ? null : c.category)}
          className={`group grid w-full grid-cols-[8rem_1fr_7rem] items-center gap-3 rounded px-1 py-0.5 text-left text-xs hover:bg-slate-50 ${selected === c.category ? 'bg-slate-100' : ''}`}
          title={`${c.category}: ${c.concepts.toLocaleString()} concepts on ${c.artworkLinks.toLocaleString()} artwork links; ${c.merged.toLocaleString()} merged from several names`}
        >
          <span className="truncate text-slate-700">{c.category}</span>
          <span className="h-3 rounded-sm bg-slate-100">
            <span
              className="block h-full rounded-sm"
              style={{ width: `${(100 * c.artworkLinks) / max}%`, background: categoryColor(c.category === 'uncategorised' ? null : c.category) }}
            />
          </span>
          <span className="text-right tabular-nums text-slate-500">
            {c.artworkLinks.toLocaleString()} <span className="text-slate-400">· {c.concepts.toLocaleString()}</span>
          </span>
        </button>
      ))}
      <div className="grid grid-cols-[8rem_1fr_7rem] gap-3 px-1 pt-1 text-[10px] text-slate-400">
        <span />
        <span>artwork links</span>
        <span className="text-right">links · concepts</span>
      </div>
    </div>
  )
}

/**
 * How categories connect: concept-to-concept edges between two categories, as a share of all
 * edges touching the row's category. One hue, light to dark.
 */
function AffinityMatrix({ overview, onSelect }: { overview: GraphOverview; onSelect: (c: string) => void }) {
  const cats = overview.categories.slice(0, 12).map((c) => c.category)
  const [hover, setHover] = useState<{ a: string; b: string; v: number; n: number } | null>(null)
  const { edges, rowTotal } = useMemo(() => {
    const edges = new Map<string, number>()
    const rowTotal = new Map<string, number>()
    for (const e of overview.affinity) {
      edges.set(`${e.a}|${e.b}`, e.edges)
      edges.set(`${e.b}|${e.a}`, e.edges)
      rowTotal.set(e.a, (rowTotal.get(e.a) ?? 0) + e.edges)
      if (e.a !== e.b) rowTotal.set(e.b, (rowTotal.get(e.b) ?? 0) + e.edges)
    }
    return { edges, rowTotal }
  }, [overview])
  if (cats.length < 2) return <p className="text-sm text-slate-500">Not enough categories yet.</p>
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-[2px] text-[10px]">
          <thead>
            <tr>
              <th />
              {cats.map((c) => (
                <th key={c} className="h-20 w-7 align-bottom font-normal text-slate-500">
                  <div className="mx-auto w-3 origin-bottom-left translate-x-2 -rotate-45 whitespace-nowrap">{c}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cats.map((a) => (
              <tr key={a}>
                <th className="pr-2 text-right font-normal text-slate-600">
                  <button className="hover:underline" onClick={() => onSelect(a)}>{a}</button>
                </th>
                {cats.map((b) => {
                  const n = edges.get(`${a}|${b}`) ?? 0
                  const v = n / Math.max(1, rowTotal.get(a) ?? 0)
                  return (
                    <td
                      key={b}
                      className="h-7 w-7 rounded-sm"
                      style={{ background: `rgba(13, 148, 136, ${Math.min(1, 0.06 + v * 2.2)})` }}
                      onMouseEnter={() => setHover({ a, b, v, n })}
                      onMouseLeave={() => setHover(null)}
                    />
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="h-4 text-xs text-slate-600">
        {hover
          ? `${hover.a} → ${hover.b}: ${hover.n.toLocaleString()} links, ${(hover.v * 100).toFixed(1)}% of ${hover.a}'s links`
          : 'Hover a cell. Darker = a bigger share of the row category’s links go to the column category.'}
      </p>
    </div>
  )
}

function Overview({ onWalk }: { onWalk: (ucid: string, name: string) => void }) {
  const { data: overview, isLoading } = useGraphOverview()
  const [category, setCategory] = useState<string | null>(null)
  if (isLoading || !overview) return <Skeleton className="h-96 w-full" />
  const shown = category ? [category] : overview.categories.slice(0, 8).map((c) => c.category)
  const relations = overview.relations.reduce<Record<string, number>>((o, r) => ({ ...o, [r.relationType]: (o[r.relationType] ?? 0) + r.n }), {})

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">The graph by category</CardTitle>
            <CardDescription>How much of the catalogue each category describes. Click one to see its biggest concepts.</CardDescription>
          </CardHeader>
          <CardContent>
            <CategoryBars overview={overview} selected={category} onSelect={setCategory} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">How categories connect</CardTitle>
            <CardDescription>Concept-to-concept links between the twelve largest categories (the weakest tier left out).</CardDescription>
          </CardHeader>
          <CardContent>
            <AffinityMatrix overview={overview} onSelect={setCategory} />
            {Object.keys(relations).length > 0 && (
              <div className="mt-3 border-t pt-3 text-xs text-slate-600">
                Typed relations added by people and the model:{' '}
                {Object.entries(relations).map(([t, n], i) => (
                  <span key={t}>{i > 0 && ' · '}{RELATION_LABEL[t]?.[0] ?? t} <b className="tabular-nums">{n.toLocaleString()}</b></span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
          <div className="space-y-1.5">
            <CardTitle className="text-sm">{category ? `Biggest ${category} concepts` : 'Start from a big concept'}</CardTitle>
            <CardDescription>Click any concept to explore its neighbourhood.</CardDescription>
          </div>
          {category && (
            <button className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" onClick={() => setCategory(null)}>
              <X className="h-3 w-3" /> all categories
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {shown.map((cat) => (
            <div key={cat} className="flex flex-wrap items-center gap-1.5">
              <span className="w-28 shrink-0 text-xs text-slate-500">{cat}</span>
              {overview.top.filter((t) => t.category === cat).map((t) => (
                <WalkChip key={t.ucid} c={t} onWalk={onWalk} />
              ))}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

/** The panel beside the graph: what the centred concept is, and where you can go from it. */
function ConceptPanel({ ucid, onWalk }: { ucid: string; onWalk: (ucid: string, name: string) => void }) {
  const { data, isLoading } = useConcept(ucid)
  if (isLoading || !data) return <Skeleton className="h-[520px] w-full" />
  const c = data.concept
  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold leading-tight">{c.name}</h2>
          <Link to={`/app/knowledge/${c.ucid}`} className="inline-flex shrink-0 items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
            Full page <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <CategoryBadge category={c.category} />
          <span className="tabular-nums">{c.artworkCount.toLocaleString()} artworks</span>
          <span className="tabular-nums">{c.degree.toLocaleString()} links</span>
          <span>weight <WeightLabel weight={c.weight} curated={c.curatorWeight != null} /></span>
        </div>
        {c.memberNames.length > 1 && (
          <p className="text-xs text-slate-500">
            Also: {c.memberNames.filter((m) => m !== c.name).slice(0, 8).join(', ')}
            {c.memberNames.length > 9 && ` +${c.memberNames.length - 9}`}
          </p>
        )}
      </div>

      {data.artworks.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5">
          {data.artworks.slice(0, 8).map((a) => (
            <Link key={a.id} to={`/app/artworks/${a.id}`} title={`${a.name}${a.artistName ? ` — ${a.artistName}` : ''}`}>
              <img src={artworkApi.getArtworkImageUrl(a.id)} alt={a.name} loading="lazy" className="aspect-square w-full rounded border object-cover" />
            </Link>
          ))}
        </div>
      )}

      {data.relations.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-slate-500">Typed relations</div>
          <div className="space-y-1">
            {data.relations.map((r) => (
              <div key={`${r.sourceUcid}-${r.targetUcid}-${r.relationType}`} className="flex items-center gap-2 text-xs">
                <span className="w-24 shrink-0 text-slate-500">{RELATION_LABEL[r.relationType]?.[r.direction === 'out' ? 0 : 1] ?? r.relationType}</span>
                <WalkChip c={r} onWalk={onWalk} />
              </div>
            ))}
          </div>
        </div>
      )}

      {data.coOccurring.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-slate-500">Appears on the same artworks as</div>
          <div className="flex flex-wrap gap-1.5">
            {data.coOccurring.slice(0, 12).map((n) => (
              <WalkChip key={n.ucid} c={n} onWalk={onWalk} extra={n.shared != null ? `${n.shared}×` : undefined} />
            ))}
          </div>
        </div>
      )}

      {data.proposals.length > 0 && (
        <Link
          to={`/app/knowledge/${c.ucid}`}
          className="block rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 hover:bg-amber-100"
        >
          {data.proposals.length} pending model proposal{data.proposals.length === 1 ? '' : 's'} touch this concept
          ({[...new Set(data.proposals.map((p) => p.action))].join(', ')}) — review on its page →
        </Link>
      )}
    </div>
  )
}

export function GraphExplorerPage() {
  const [params, setParams] = useSearchParams()
  const ucid = params.get('c') || undefined
  const neighbours = Number(params.get('n')) || 30
  const artworks = Number(params.get('a')) || 0
  const [trail, setTrail] = useState<Array<{ ucid: string; name: string }>>([])
  const { data: graph, isLoading, isFetching } = useConceptGraph(ucid, neighbours, artworks)

  const set = (next: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    setParams(p)
  }
  // Opened from a link (no trail yet): the trail starts at the centred concept.
  const centre = graph?.nodes.find((n) => n.centre)
  const crumbs = trail.length ? trail : centre?.ucid ? [{ ucid: centre.ucid, name: centre.name }] : []
  const walk = (to: string, name?: string) => {
    if (to === ucid) return
    const label = name ?? graph?.nodes.find((n) => n.ucid === to)?.name ?? to
    const at = crumbs.findIndex((t) => t.ucid === to)
    setTrail(at >= 0 ? crumbs.slice(0, at + 1) : [...crumbs, { ucid: to, name: label }].slice(-12))
    set({ c: to })
  }
  const home = () => {
    setTrail([])
    set({ c: undefined })
  }

  return (
    <KnowledgeFrame
      title="Graph explorer"
      description={
        <>
          Search the graph and walk it. Pick a concept to see its neighbourhood — the concepts it is closest to,
          sized by how many artworks carry them and coloured by category — then select a node to inspect its connections and explore its neighbourhood.
          The trail above the graph takes you back.
        </>
      }
    >
      <ConceptSearch onPick={walk} />

      {!ucid && <Overview onWalk={walk} />}

      {ucid && (
        <>
          <div className="flex flex-wrap items-center gap-1 text-sm">
            <button onClick={home} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900">
              <ArrowLeft className="h-3.5 w-3.5" /> Overview
            </button>
            {crumbs.map((t, i) => (
              <span key={t.ucid} className="inline-flex items-center gap-1">
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                {i === crumbs.length - 1 ? (
                  <span className="font-medium">{t.name}</span>
                ) : (
                  <button className="text-slate-500 hover:text-slate-900 hover:underline" onClick={() => walk(t.ucid, t.name)}>{t.name}</button>
                )}
              </span>
            ))}
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
            <Card className="min-w-0">
              <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-2">
                <CardTitle className="text-sm">Neighbourhood</CardTitle>
                <div className="flex items-center gap-3 text-xs text-slate-600">
                  <label className="flex items-center gap-1.5">
                    Concepts
                    <select className="rounded border bg-white px-1.5 py-0.5" value={neighbours} onChange={(e) => set({ n: e.target.value })}>
                      {[15, 30, 50, 80].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5">
                    Artworks
                    <select className="rounded border bg-white px-1.5 py-0.5" value={artworks} onChange={(e) => set({ a: e.target.value === '0' ? undefined : e.target.value })}>
                      {[0, 12, 24, 40].map((v) => <option key={v} value={v}>{v === 0 ? 'none' : v}</option>)}
                    </select>
                  </label>
                </div>
              </CardHeader>
              <CardContent className={isFetching ? 'opacity-70' : undefined}>
                {isLoading && <Skeleton className="h-[560px] w-full" />}
                {graph && <ConceptGraph graph={graph} height={560} onConceptClick={(u) => walk(u)} />}
                {graph && (
                  <p className="mt-2 text-xs text-slate-500">
                    Switch between force, category and hierarchy layouts. The Ontology lens shows stored typed relations; Similarity shows semantic neighbours. Select a node to inspect direction and provenance, then explore its neighbourhood.
                  </p>
                )}
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardContent className="p-4">
                <ConceptPanel ucid={ucid} onWalk={walk} />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </KnowledgeFrame>
  )
}
